/**
 * @module ui.list
 * @version 4.9.0
 * @description Filterable/sortable list Web Component
 * 
 * Changelog:
 * - 4.9.0: FIX - Discrete field match counts now include range + threshold filters
 *          #calculateAllMatchCounts() passes rangeFiltersForApi + activeThresholdFilters
 *          to calculateAllMatchCounts() in api.list.js (v4.4.0)
 *          Fixes: "Auf Lager 7" count not updating when threshold "Top bewertet" is active
 * - 4.8.0: Added threshold filter support (threshold-min, threshold-max)
 *          New: #activeThresholdFilters Map for numeric threshold filters
 *          New: #filterModes Map tracks mode per field (from filter registration)
 *          Both modes route through numeric threshold path in api.list.js
 *          threshold-min: itemValue ≥ lowest checked (e.g., rating ≥ 4)
 *          threshold-max: itemValue ≤ highest checked (e.g., distance ≤ 5000m)
 *          Uses filterItemsCombined() and calculateThresholdMatchCounts() from api.list.js
 * - 4.7.0: AUDIT FIX - Quality Checks v4.5 compliance
 *          Added ROLES, STATES, EVENTS, SETTINGS constants (all frozen)
 *          Added LISTEN constant for 6 external events
 *          Replaced all string literals with constants
 *          Converted global emit() → this.emit() for own events
 *          Fixed export: { LiftrList, ROLES, STATES, EVENTS, SETTINGS }
 * - 4.6.0: BREAKING - CSS order instead of DOM manipulation for sorting
 *          Static blocks keep their position via liftr-list-index attribute
 *          Removed #isReordering guard (no longer needed)
 *          Added #staticPositions Set to track reserved positions
 * - 4.5.1: FIX - Added #checkInitialDistanceState() for saved PLZ timing
 *          Stores may emit distance-changed before list mounts
 *          Now checks existing store states on mount to enable distance sort
 * - 4.5.0: BREAKING - Removed static content repositioning
 *          Static blocks now stay at SSR position (no layout shift)
 *          Removed #staticElements, #repositionStaticContent()
 * - 4.4.0: Added distance sort trigger disabled state
 *          Sort triggers with distance fields are disabled until PLZ is entered
 *          Triggers get liftr-state="disabled" initially, removed when valid PLZ entered
 *          State toggles both ways: disabled when PLZ cleared, enabled when PLZ valid
 * - 4.3.1: FIX - Added #isReordering guard to prevent infinite loop
 *          DOM reorder triggers store reconnect → events → refilter → loop
 *          Guard prevents event handlers from triggering during reorder
 * - 4.3.0: Added sidebar-close button disabled state
 *          Button gets liftr-state="disabled" when visibleCount === 0
 * - 4.2.0: Added persist filter support
 *          Persist range filters excluded from count and clearFilters()
 * - 4.1.1: FIX - Added liftr:store:distance-changed handler
 *          List now re-filters when PLZ is entered and distance values change
 * - 4.1.0: BREAKING - Removed manual liftr:list:mounted event (now automatic via LiftrModule)
 *          Added range filter support via liftr:list-range:changed event
 *          Listeners should use `rootId` instead of `listId` in event payload
 * - 4.0.2: Fixed timing issue - emits liftr:list:mounted for late registration
 * - 4.0.1: Added clear button support via liftr-role="list-clear"
 * - 4.0.0: Initial v4.0 implementation
 * 
 * Replaces Finsweet CMS Filter for client-side filtering of Shopify Metaobjects (~100 items).
 * Coordinates filter/sort child components, manages visibility, and updates counts.
 * 
 * STATIC CONTENT:
 * Static blocks (discount, featured) are positioned by Liquid at render time.
 * They have a liftr-list-index attribute specifying their fixed position.
 * When sorting, CSS order is used instead of DOM manipulation.
 * Static blocks keep their position, list items flow around them.
 * 
 * FILTER TYPES:
 * - Discrete filters (checkbox/radio): via liftr:list-filter:changed
 * - Range filters (slider): via liftr:list-range:changed
 *   - Supports `persist` flag to exclude from count and clear
 * - Threshold filters (checkbox numeric): via liftr:list-filter:changed with mode
 *   - threshold-min: items where field value ≥ threshold (e.g., rating ≥ 4)
 *   - threshold-max: items where field value ≤ threshold (e.g., distance ≤ 5000m)
 * 
 * DYNAMIC VALUE UPDATES:
 * - Opening status: via liftr:store:opening-changed
 * - Distance values: via liftr:store:distance-changed
 * 
 * DISTANCE SORT TRIGGERS:
 * - Sort triggers with liftr-value containing "distance" are disabled initially
 * - Enabled automatically when first distance data is received (PLZ entered)
 * - Uses liftr-state="disabled" and aria-disabled attribute
 * - On mount: checks existing store states for saved PLZ (Late Mount Pattern)
 * 
 * LIFECYCLE EVENTS (automatic from LiftrModule):
 * - liftr:list:mounted  → After mount(), payload: { element, root, rootId }
 * - liftr:list:unmounted → Before unmount(), payload: { element, root, rootId }
 * 
 * @element liftr-list
 * 
 * @fires liftr:list:mounted - (Automatic) After mount, triggers filter re-registration
 * @fires liftr:list:unmounted - (Automatic) Before unmount
 * @fires liftr:list:updated - After filter/sort applied
 * @fires liftr:list:ready - After initial mount with item count
 * @fires liftr:list:cleared - After filters cleared via clear button
 * 
 * @listens liftr:list-filter:changed - Discrete filter value changes
 * @listens liftr:list-range:changed - Range filter value changes
 * @listens liftr:list-sort:changed - Sort field/direction changes
 * @listens liftr:list-filter:register - Filter field registration
 * @listens liftr:store:opening-changed - Store opening status updates
 * @listens liftr:store:distance-changed - Store distance value updates
 * 
 * @attr {string} liftr-id - Required identifier for targeting
 * @attr {string} liftr-state - State output: filtered, empty, loading
 * 
 * @slot - List items with liftr-role="list-item"
 * 
 * @example
 * <liftr-list liftr-id="store-list">
 *   <div liftr-role="list-wrapper">
 *     <div liftr-role="list-item">
 *       <span liftr-field="opening" liftr-value="open">Geöffnet</span>
 *       <span liftr-field="distance_km" liftr-value="12">12 km</span>
 *       Store 1
 *     </div>
 *     <div liftr-list-index="3">Static Block at position 3</div>
 *     <div liftr-role="list-item">
 *       <span liftr-field="opening" liftr-value="closed">Geschlossen</span>
 *       <span liftr-field="distance_km" liftr-value="25">25 km</span>
 *       Store 2
 *     </div>
 *   </div>
 *   <div liftr-role="list-empty">No results found</div>
 * </liftr-list>
 * 
 * <!-- Sort trigger for distance (disabled until PLZ entered) -->
 * <button liftr-role="sort-trigger" liftr-target="store-list" liftr-value="distance_m:asc">
 *   Entfernung
 * </button>
 * 
 * <!-- Discrete filter -->
 * <liftr-list-filter liftr-target="store-list" liftr-field="opening">...</liftr-list-filter>
 * 
 * <!-- Threshold filter: Rating ≥ 4 -->
 * <liftr-list-filter liftr-target="store-list" liftr-field="rating" liftr-mode="threshold">...</liftr-list-filter>
 * 
 * <!-- Threshold filter: Distance ≤ 5000m -->
 * <liftr-list-filter liftr-target="store-list" liftr-field="distance_m" liftr-mode="threshold-max">...</liftr-list-filter>
 * 
 * <!-- Range filter (persist = excluded from count/clear) -->
 * <liftr-range liftr-target="store-list" liftr-field="distance_km" liftr-persist="true" max="60">...</liftr-range>
 */

import { LiftrModule } from './core.module.js';
import { ATTRIBUTES, on } from './core.liftr.js';
import { roleAllGlobal } from './core.utilities.js';
import { 
  filterItems, 
  filterItemsWithRanges, 
  filterItemsCombined,
  sortItems, 
  calculateAllMatchCounts,
  calculateThresholdMatchCounts
} from './api.list.js';

/* ═══════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * ALWAYS use these in this.role() / this.roleAll() / roleAllGlobal().
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  WRAPPER:       'list-wrapper',
  ITEM:          'list-item',
  COUNT_VISIBLE: 'list-count-visible',
  COUNT_TOTAL:   'list-count-total',
  COUNT_FILTERS: 'list-count-filters',
  CLEAR:         'list-clear',
  SORT_TRIGGER:  'sort-trigger'
});

/**
 * State tokens for liftr-state attribute.
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  HIDDEN:       'hidden',
  EMPTY:        'empty',
  FILTERED:     'filtered',
  DISABLED:     'disabled'
});

/**
 * Event action names for this.emit().
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  READY:   'ready',
  UPDATED: 'updated',
  CLEARED: 'cleared'
});

/**
 * Settings tokens for liftr-settings attribute.
 * Empty for this module (no configurable settings tokens).
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({});

/**
 * External events this module listens to.
 * Full event strings — used in on() calls.
 * NOT exported — internal to module only.
 * @private
 */
const LISTEN = Object.freeze({
  FILTER_CHANGED:         'liftr:list-filter:changed',
  RANGE_CHANGED:          'liftr:list-range:changed',
  SORT_CHANGED:           'liftr:list-sort:changed',
  FILTER_REGISTER:        'liftr:list-filter:register',
  STORE_OPENING_CHANGED:  'liftr:store:opening-changed',
  STORE_DISTANCE_CHANGED: 'liftr:store:distance-changed'
});

/* ═══════════════════════════════════════════════════════════════════
 * INTERNAL CONSTANTS
 * ═══════════════════════════════════════════════════════════════════ */

/** Valid distance states that indicate PLZ was found */
const VALID_DISTANCE_STATES = /distance-(near|mid|far|remote)/;

/** Module-specific attribute for static block positioning */
const ATTR_LIST_INDEX = 'liftr-list-index';

/** Order value for hidden items (ensures they appear last) */
const ORDER_HIDDEN = 9999;

/** Threshold mode short forms (stripped of "threshold-" prefix for api.list.js) */
const THRESHOLD_MODES = Object.freeze({
  'threshold-min': 'min',
  'threshold-max': 'max'
});

/* ═══════════════════════════════════════════════════════════════════
 * LIST WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

class LiftrList extends LiftrModule {
  
  static NAME = 'list';
  
  static DEFAULTS = {};
  
  /* ═══════════════════════════════════════════════════════════════════
   * PRIVATE STATE
   * ═══════════════════════════════════════════════════════════════════ */
  
  /** @type {Element[]} All list-item elements */
  #items = [];
  
  /** @type {Map<string, Set<string>>} field → Set of active values (discrete filters) */
  #activeFilters = new Map();
  
  /** @type {Map<string, { max: number, persist: boolean }>} field → { max, persist } (range filters) */
  #activeRangeFilters = new Map();
  
  /** @type {Map<string, { value: number, mode: 'min'|'max' }>} field → { value, mode } (threshold filters) */
  #activeThresholdFilters = new Map();
  
  /** @type {{ key: string, direction: 'asc'|'desc' }|null} Active sort */
  #activeSort = null;
  
  /** @type {Element|null} List wrapper element */
  #wrapper = null;
  
  /** @type {Map<string, string[]>} field → possible values (for match counts) */
  #fieldRegistry = new Map();
  
  /** @type {Map<string, string|null>} field → mode ('threshold-min', 'threshold-max', or null) */
  #filterModes = new Map();
  
  /** @type {Set<number>} Reserved positions for static blocks */
  #staticPositions = new Set();
  
  /** @type {Element[]} Sort triggers for distance fields */
  #distanceSortTriggers = [];
  
  /** @type {boolean} Whether distance data is available */
  #hasDistanceData = false;
  
  /* ═══════════════════════════════════════════════════════════════════
   * LIFECYCLE
   * ═══════════════════════════════════════════════════════════════════ */
  
  mount() {
    // Cache DOM references
    this.#wrapper = this.role(ROLES.WRAPPER);
    this.#items = this.roleAll(ROLES.ITEM);
    
    // Cache static block positions and set their CSS order
    this.#initStaticPositions();
    
    // Listen for discrete filter changes
    this.addDisposable(
      on(LISTEN.FILTER_CHANGED, this.#handleFilterChange.bind(this))
    );
    
    // Listen for range filter changes
    this.addDisposable(
      on(LISTEN.RANGE_CHANGED, this.#handleRangeChange.bind(this))
    );
    
    // Listen for sort changes
    this.addDisposable(
      on(LISTEN.SORT_CHANGED, this.#handleSortChange.bind(this))
    );
    
    // Listen for filter registration (to build field registry)
    this.addDisposable(
      on(LISTEN.FILTER_REGISTER, this.#handleFilterRegister.bind(this))
    );
    
    // Listen for store opening changes (dynamic field updates)
    // Recalculate filters when opening status changes (every minute)
    this.addDisposable(
      on(LISTEN.STORE_OPENING_CHANGED, this.#handleStoreOpeningChange.bind(this))
    );
    
    // Listen for store distance changes (PLZ eingabe)
    // Recalculate filters when distance values change
    this.addDisposable(
      on(LISTEN.STORE_DISTANCE_CHANGED, this.#handleStoreDistanceChange.bind(this))
    );
    
    // Listen for clear button clicks (document-level delegation)
    const clearHandler = this.#handleClearClick.bind(this);
    document.addEventListener('click', clearHandler);
    this.addDisposable(() => document.removeEventListener('click', clearHandler));
    
    // Cache distance sort triggers and disable initially
    this.#distanceSortTriggers = this.#findDistanceSortTriggers();
    this.#updateDistanceSortState(false);
    
    // Initial counts update
    this.#updateCounts(this.#items.length);
    
    // ═══════════════════════════════════════════════════════════════════
    // LATE MOUNT PATTERN: Check for existing distance data
    // ═══════════════════════════════════════════════════════════════════
    // Stores may have already computed distance from saved PLZ before
    // this list mounted. Check their current state to sync up.
    this.#checkInitialDistanceState();
    
    // NOTE: liftr:list:mounted is now emitted automatically by LiftrModule
    // after mount() completes. Filters can listen for it using:
    //   on('liftr:list:mounted', (e) => { if (e.detail.rootId === targetId) ... })
    
    // Emit ready event with initial state (custom payload for consumers)
    this.emit(EVENTS.READY, {
      total: this.#items.length,
      visible: this.#items.length
    });
  }
  
  unmount() {
    // Reset CSS order on items
    for (const item of this.#items) {
      item.style.order = '';
    }
    
    // Reset CSS order on static blocks
    if (this.#wrapper) {
      const statics = this.#wrapper.querySelectorAll(`[${ATTR_LIST_INDEX}]`);
      for (const el of statics) {
        el.style.order = '';
      }
    }
    
    this.#items = [];
    this.#activeFilters.clear();
    this.#activeRangeFilters.clear();
    this.#activeThresholdFilters.clear();
    this.#activeSort = null;
    this.#wrapper = null;
    this.#fieldRegistry.clear();
    this.#filterModes.clear();
    this.#staticPositions.clear();
    this.#distanceSortTriggers = [];
    this.#hasDistanceData = false;
    super.unmount();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * STATIC BLOCK POSITIONING
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Initialize static block positions
   * Caches positions and sets fixed CSS order values
   * @private
   */
  #initStaticPositions() {
    if (!this.#wrapper) return;
    
    const statics = this.#wrapper.querySelectorAll(`[${ATTR_LIST_INDEX}]`);
    
    for (const el of statics) {
      const index = parseInt(el.getAttribute(ATTR_LIST_INDEX)) || 0;
      if (index > 0) {
        el.style.order = String(index);
        this.#staticPositions.add(index);
      }
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * EVENT HANDLERS
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Handle clear button click (document-level delegation)
   * @param {MouseEvent} event
   * @private
   */
  #handleClearClick(event) {
    // Find clear button (could be the target or a parent)
    const clearBtn = event.target.closest(`[liftr-role~="${ROLES.CLEAR}"]`);
    if (!clearBtn) return;
    
    // Check if this clear button targets us
    const targetId = clearBtn.getAttribute(ATTRIBUTES.TARGET);
    const listId = this.getAttribute(ATTRIBUTES.ID);
    
    if (targetId !== listId) return;
    
    // Clear all filters (respects persist flag)
    this.clearFilters();
    
    // Emit cleared event for filter components to reset their inputs
    this.emit(EVENTS.CLEARED, {
      listId: listId
    });
  }
  
  /**
   * Handle filter registration from liftr-list-filter
   * Stores possible values and mode per field for match count calculation
   * @param {CustomEvent} event
   * @private
   */
  #handleFilterRegister(event) {
    const { target: targetId, field, values, mode } = event.detail;
    
    // Only process if this event is for us
    if (targetId !== this.getAttribute(ATTRIBUTES.ID)) return;
    
    // Register field with its possible values
    if (field && values && values.length > 0) {
      this.#fieldRegistry.set(field, values);
      
      // Store filter mode if present (threshold-min, threshold-max, or null)
      this.#filterModes.set(field, mode || null);
      
      // After registration, emit updated event with fresh match counts
      // This ensures the filter gets accurate counts
      this.#emitUpdatedWithCounts();
    }
  }
  
  /**
   * Handle discrete filter change from liftr-list-filter
   * 
   * Routes to different filter stores based on mode:
   * - null (standard): discrete filter (OR matching)
   * - 'threshold-min': discrete filter (filter expands values, OR matching)
   * - 'threshold-max': threshold filter (numeric ≤ comparison)
   * 
   * @param {CustomEvent} event
   * @private
   */
  #handleFilterChange(event) {
    const { target: targetId, field, values, mode } = event.detail;
    
    // Only process if this event is for us
    if (targetId !== this.getAttribute(ATTRIBUTES.ID)) return;
    
    // Route based on mode
    const shortMode = THRESHOLD_MODES[mode];
    
    if (shortMode) {
      // ═══════════════════════════════════════════════════════════════
      // THRESHOLD-MIN / THRESHOLD-MAX: Numeric comparison via api.list.js
      // threshold-min: itemValue ≥ threshold (e.g., rating ≥ 4)
      // threshold-max: itemValue ≤ threshold (e.g., distance ≤ 5000)
      // ═══════════════════════════════════════════════════════════════
      // Remove from discrete filters (threshold uses its own store)
      this.#activeFilters.delete(field);
      
      if (values && values.length > 0) {
        const numericValues = values.map(Number).filter(Number.isFinite);
        
        if (numericValues.length > 0) {
          // threshold-min: use lowest checked value (≥ lowest)
          // threshold-max: use highest checked value (≤ highest)
          const thresholdValue = shortMode === 'min'
            ? Math.min(...numericValues)
            : Math.max(...numericValues);
          
          this.#activeThresholdFilters.set(field, {
            value: thresholdValue,
            mode: shortMode
          });
        }
      } else {
        this.#activeThresholdFilters.delete(field);
      }
    } else {
      // ═══════════════════════════════════════════════════════════════
      // STANDARD: Store as discrete filter (OR matching)
      // ═══════════════════════════════════════════════════════════════
      this.#activeThresholdFilters.delete(field);
      
      if (values && values.length > 0) {
        this.#activeFilters.set(field, new Set(values));
      } else {
        this.#activeFilters.delete(field);
      }
    }
    
    // Apply filters and sort
    this.#applyFiltersAndSort();
  }
  
  /**
   * Handle range filter change from liftr-range
   * Supports persist flag for count/clear exclusion
   * @param {CustomEvent} event
   * @private
   */
  #handleRangeChange(event) {
    const { target: targetId, field, max, persist } = event.detail;
    
    // Only process if this event is for us
    if (targetId !== this.getAttribute(ATTRIBUTES.ID)) return;
    
    // Update range filter state (include persist flag)
    if (Number.isFinite(max)) {
      this.#activeRangeFilters.set(field, { 
        max, 
        persist: persist ?? false 
      });
    } else {
      this.#activeRangeFilters.delete(field);
    }
    
    // Apply filters and sort
    this.#applyFiltersAndSort();
  }
  
  /**
   * Handle sort change from liftr-list-sort
   * @param {CustomEvent} event
   * @private
   */
  #handleSortChange(event) {
    const { target: targetId, key, direction } = event.detail;
    
    // Only process if this event is for us
    if (targetId !== this.getAttribute(ATTRIBUTES.ID)) return;
    
    // Update sort state
    if (key) {
      this.#activeSort = { key, direction: direction || 'asc' };
    } else {
      this.#activeSort = null;
    }
    
    // Apply filters and sort
    this.#applyFiltersAndSort();
  }
  
  /**
   * Handle store opening status change
   * Recalculates filters when liftr-field="opening" values change dynamically
   * 
   * @param {CustomEvent} event
   * @private
   */
  #handleStoreOpeningChange(event) {
    // Only recalculate if we have an opening filter registered
    if (!this.#fieldRegistry.has('opening')) return;
    
    // Only recalculate if opening filter is active
    // (Avoids unnecessary work when no opening filter is selected)
    if (!this.#activeFilters.has('opening') && this.#activeFilters.size === 0 && this.#activeRangeFilters.size === 0) {
      // No filters active, but still need to update match counts
      this.#emitUpdatedWithCounts();
      return;
    }
    
    // Re-apply filters (opening values may have changed)
    this.#applyFiltersAndSort();
  }
  
  /**
   * Handle store distance change
   * Recalculates filters when liftr-field="distance_km" values change dynamically
   * 
   * Updates distance sort trigger state based on valid property:
   * - valid: true → PLZ found, enable distance sorting
   * - valid: false → PLZ cleared/not found, disable distance sorting
   * 
   * @param {CustomEvent} event
   * @private
   */
  #handleStoreDistanceChange(event) {
    const { valid } = event.detail;
    
    // Update distance sort triggers when state changes
    // First event wins per change cycle (all stores share same PLZ input)
    if (valid !== this.#hasDistanceData) {
      this.#hasDistanceData = valid;
      this.#updateDistanceSortState(valid);
    }
    
    // Only re-filter if distance filter is relevant
    const hasDistanceFilter = this.#activeRangeFilters.has('distance_km') || 
                              this.#activeRangeFilters.has('distance_m') ||
                              this.#activeThresholdFilters.has('distance_km') ||
                              this.#activeThresholdFilters.has('distance_m') ||
                              this.#fieldRegistry.has('distance_km') ||
                              this.#fieldRegistry.has('distance_m');
    
    if (!hasDistanceFilter) return;
    
    // Re-apply filters (distance values in items have changed)
    this.#applyFiltersAndSort();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * DISTANCE SORT TRIGGERS
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Find all sort triggers targeting distance fields
   * Matches liftr-value containing "distance" (e.g., "distance_m:asc", "distance_km:desc")
   * @returns {Element[]}
   * @private
   */
  #findDistanceSortTriggers() {
    const listId = this.getAttribute(ATTRIBUTES.ID);
    
    return roleAllGlobal(ROLES.SORT_TRIGGER).filter(trigger => {
      // Check liftr-target on trigger itself OR closest parent with it
      // (sort triggers inside <liftr-list-sort> inherit parent's target)
      const targetEl = trigger.closest(`[${ATTRIBUTES.TARGET}]`);
      const targetId = targetEl?.getAttribute(ATTRIBUTES.TARGET);
      if (targetId !== listId) return false;
      
      // Must be distance-based sort (e.g. "distance_m:asc")
      const value = trigger.getAttribute(ATTRIBUTES.VALUE) || '';
      return value.includes('distance');
    });
  }
  
  /**
   * Update disabled state of distance sort triggers
   * @param {boolean} enabled - Whether distance sorting should be enabled
   * @private
   */
  #updateDistanceSortState(enabled) {
    for (const trigger of this.#distanceSortTriggers) {
      trigger.disabled = !enabled;
    }
  }
  
  /**
   * Check if any store already has valid distance data (Late Mount Pattern)
   * 
   * Handles timing issue where stores compute distance from saved PLZ
   * before this list mounts. Checks existing store states to sync up.
   * 
   * @private
   */
  #checkInitialDistanceState() {
    // Query stores within this list's items
    const stores = this.querySelectorAll('liftr-store');
    
    for (const store of stores) {
      const state = store.getAttribute(ATTRIBUTES.STATE) || '';
      
      // Check for valid distance state (not unset/unknown)
      if (VALID_DISTANCE_STATES.test(state)) {
        this.#hasDistanceData = true;
        this.#updateDistanceSortState(true);
        return;  // First valid store is enough
      }
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * FILTER & SORT LOGIC
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Emit updated event with current match counts
   * Used after filter registration to send initial counts
   * @private
   */
  #emitUpdatedWithCounts() {
    const visibleCount = this.#items.filter(item => {
      const state = item.getAttribute(ATTRIBUTES.STATE) || '';
      return !state.includes(STATES.HIDDEN);
    }).length;
    
    const matchCounts = this.#calculateAllMatchCounts();
    
    this.emit(EVENTS.UPDATED, {
      visible: visibleCount,
      total: this.#items.length,
      filters: this.#filtersToObject(),
      sort: this.#activeSort,
      matchCounts
    });
  }
  
  /**
   * Apply current filters and sort, then update DOM
   * @private
   */
  #applyFiltersAndSort() {
    // Build range filters Map for api.list.js (only needs max values)
    const rangeFiltersForApi = new Map();
    for (const [field, config] of this.#activeRangeFilters) {
      rangeFiltersForApi.set(field, { max: config.max });
    }
    
    // 1. Filter items (discrete + range + threshold)
    const visibleSet = filterItemsCombined(
      this.#items,
      this.#activeFilters,
      rangeFiltersForApi,
      this.#activeThresholdFilters
    );
    let visibleItems = [...visibleSet];
    
    // 2. Sort if active
    if (this.#activeSort) {
      visibleItems = sortItems(visibleItems, this.#activeSort.key, this.#activeSort.direction);
    }
    
    // 3. Update item visibility via state
    this.#updateItemVisibility(visibleSet);
    
    // 4. Update CSS order for sorted items
    this.#updateItemOrder(visibleItems);
    
    // 5. Update counts
    this.#updateCounts(visibleItems.length);
    
    // 6. Update list state
    this.#updateListState(visibleItems.length);
    
    // 7. Calculate and emit match counts for all registered fields
    const matchCounts = this.#calculateAllMatchCounts();
    
    // 8. Emit updated event
    this.emit(EVENTS.UPDATED, {
      visible: visibleItems.length,
      total: this.#items.length,
      filters: this.#filtersToObject(),
      sort: this.#activeSort,
      matchCounts
    });
  }
  
  /**
   * Update item visibility using liftr-state
   * @param {Set<Element>} visibleSet
   * @private
   */
  #updateItemVisibility(visibleSet) {
    for (const item of this.#items) {
      const itemState = this.createState(item);
      
      if (visibleSet.has(item)) {
        itemState.remove(STATES.HIDDEN);
      } else {
        itemState.add(STATES.HIDDEN);
      }
    }
  }
  
  /**
   * Update CSS order for sorted items
   * 
   * Uses CSS order instead of DOM manipulation to prevent:
   * - Store reconnect events (opening-changed, distance-changed)
   * - Infinite event loops
   * - Layout thrashing
   * 
   * Static blocks have fixed order values (from liftr-list-index).
   * List items get order values that skip static positions.
   * 
   * @param {Element[]} sortedItems - Visible items in sorted order
   * @private
   */
  #updateItemOrder(sortedItems) {
    let orderIndex = 0;
    
    // Assign order to visible items, skipping static positions
    for (const item of sortedItems) {
      orderIndex++;
      
      // Skip positions reserved for static blocks
      while (this.#staticPositions.has(orderIndex)) {
        orderIndex++;
      }
      
      item.style.order = String(orderIndex);
    }
    
    // Hidden items get high order value (appear at end)
    for (const item of this.#items) {
      const state = item.getAttribute(ATTRIBUTES.STATE) || '';
      if (state.includes(STATES.HIDDEN)) {
        item.style.order = String(ORDER_HIDDEN);
      }
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * COUNT UPDATES
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Update all count elements
   * Persistent range filters are excluded from filter count
   * Threshold filters ARE counted (they're user-initiated checkbox filters)
   * @param {number} visibleCount
   * @private
   */
  #updateCounts(visibleCount) {
    const listId = this.getAttribute(ATTRIBUTES.ID);
    const totalCount = this.#items.length;
    
    // Count filters (exclude persist range filters, include threshold filters)
    const discreteCount = this.#activeFilters.size;
    const rangeCount = [...this.#activeRangeFilters.values()]
      .filter(config => !config.persist)
      .length;
    const thresholdCount = this.#activeThresholdFilters.size;
    const filterCount = discreteCount + rangeCount + thresholdCount;
    
    // Internal counts (within this element)
    const visibleEl = this.role(ROLES.COUNT_VISIBLE);
    const totalEl = this.role(ROLES.COUNT_TOTAL);
    
    if (visibleEl) visibleEl.textContent = String(visibleCount);
    if (totalEl) totalEl.textContent = String(totalCount);
    
    // External counts - single query, then filter
    const allCountElements = roleAllGlobal(ROLES.COUNT_VISIBLE)
      .concat(roleAllGlobal(ROLES.COUNT_TOTAL))
      .concat(roleAllGlobal(ROLES.COUNT_FILTERS));
    
    for (const el of allCountElements) {
      if (el.getAttribute(ATTRIBUTES.TARGET) !== listId) continue;
      
      const role = el.getAttribute(ATTRIBUTES.ROLE) || '';
      
      if (role.includes(ROLES.COUNT_VISIBLE)) {
        el.textContent = String(visibleCount);
      } else if (role.includes(ROLES.COUNT_TOTAL)) {
        el.textContent = String(totalCount);
      } else if (role.includes(ROLES.COUNT_FILTERS)) {
        el.textContent = filterCount > 0 ? String(filterCount) : '';
        el.hidden = filterCount === 0;
      }
    }

  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * LIST STATE
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Update list state based on visibility
   * Persist filters are excluded from "filtered" state calculation
   * @param {number} visibleCount
   * @private
   */
  #updateListState(visibleCount) {
    const isEmpty = visibleCount === 0;
    
    // Check if any non-persist filters are active
    const hasDiscreteFilters = this.#activeFilters.size > 0;
    const hasNonPersistRangeFilters = [...this.#activeRangeFilters.values()]
      .some(config => !config.persist);
    const hasThresholdFilters = this.#activeThresholdFilters.size > 0;
    const isFiltered = hasDiscreteFilters || hasNonPersistRangeFilters || hasThresholdFilters;
    
    // Update state on list element
    this.state.toggle(STATES.EMPTY, isEmpty);
    this.state.toggle(STATES.FILTERED, isFiltered);
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * MATCH COUNTS
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Calculate match counts for all registered fields
   * Routes to standard or threshold counting based on field mode
   * @returns {Object} field → Map(value → count)
   * @private
   */
  #calculateAllMatchCounts() {
    if (this.#fieldRegistry.size === 0) {
      return {};
    }
    
    const obj = {};
    
    // Build range filters Map for api.list.js
    const rangeFiltersForApi = new Map();
    for (const [field, config] of this.#activeRangeFilters) {
      rangeFiltersForApi.set(field, { max: config.max });
    }
    
    for (const [field, possibleValues] of this.#fieldRegistry) {
      const mode = this.#filterModes.get(field);
      const shortMode = THRESHOLD_MODES[mode];
      
      if (shortMode) {
        // Threshold field: use numeric comparison counting
        obj[field] = calculateThresholdMatchCounts(
          this.#items,
          this.#activeFilters,
          rangeFiltersForApi,
          this.#activeThresholdFilters,
          field,
          possibleValues,
          shortMode
        );
      } else {
        // Standard field: use discrete value counting
        obj[field] = calculateAllMatchCounts(
          this.#items,
          this.#activeFilters,
          new Map([[field, possibleValues]]),
          rangeFiltersForApi,
          this.#activeThresholdFilters
        ).get(field) ?? new Map();
      }
    }
    
    return obj;
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * UTILITIES
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Convert filters to plain object for event payload
   * @returns {Object}
   * @private
   */
  #filtersToObject() {
    const obj = {
      discrete: {},
      range: {},
      threshold: {}
    };
    
    for (const [field, values] of this.#activeFilters) {
      obj.discrete[field] = [...values];
    }
    
    for (const [field, config] of this.#activeRangeFilters) {
      obj.range[field] = { 
        max: config.max,
        persist: config.persist
      };
    }
    
    for (const [field, config] of this.#activeThresholdFilters) {
      obj.threshold[field] = {
        value: config.value,
        mode: config.mode
      };
    }
    
    return obj;
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * PUBLIC API
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Clear all active filters (discrete + threshold + non-persist range)
   * Persist range filters are preserved
   */
  clearFilters() {
    // Clear all discrete filters
    this.#activeFilters.clear();
    
    // Clear all threshold filters
    this.#activeThresholdFilters.clear();
    
    // Clear only non-persist range filters
    for (const [field, config] of this.#activeRangeFilters) {
      if (!config.persist) {
        this.#activeRangeFilters.delete(field);
      }
    }
    
    this.#applyFiltersAndSort();
  }
  
  /**
   * Clear active sort
   */
  clearSort() {
    this.#activeSort = null;
    this.#applyFiltersAndSort();
  }
  
  /**
   * Get current visible count
   * @returns {number}
   */
  get visibleCount() {
    return this.#items.filter(item => {
      const state = item.getAttribute(ATTRIBUTES.STATE) || '';
      return !state.includes(STATES.HIDDEN);
    }).length;
  }
  
  /**
   * Get total item count
   * @returns {number}
   */
  get totalCount() {
    return this.#items.length;
  }
  
  /**
   * Get active discrete filter count
   * @returns {number}
   */
  get filterCount() {
    return this.#activeFilters.size;
  }
  
  /**
   * Get active range filter count (excluding persist)
   * @returns {number}
   */
  get rangeFilterCount() {
    return [...this.#activeRangeFilters.values()]
      .filter(config => !config.persist)
      .length;
  }
  
  /**
   * Get active threshold filter count
   * @returns {number}
   */
  get thresholdFilterCount() {
    return this.#activeThresholdFilters.size;
  }
  
  /**
   * Get total active filter count (discrete + threshold + non-persist range)
   * @returns {number}
   */
  get totalFilterCount() {
    return this.filterCount + this.rangeFilterCount + this.thresholdFilterCount;
  }
  
  /**
   * Check if distance data is available
   * @returns {boolean}
   */
  get hasDistanceData() {
    return this.#hasDistanceData;
  }
}

/* ═══════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

customElements.define('liftr-list', LiftrList);

export { LiftrList, ROLES, STATES, EVENTS, SETTINGS };